import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Moon, Sun } from "lucide-react";

export type Theme = "light" | "dark" | "auto";
export const themes: { id: Theme; name: string; description: string }[] = [
  { id: "dark", name: "深色", description: "墨黑底色，清晰的绿色点缀。" },
  { id: "light", name: "浅色", description: "明亮底色，柔和的日常视图。" },
  { id: "auto", name: "自动", description: "跟随系统的深色或浅色设置。" },
];
const storageKey = "smarttodo.theme.v1";
const ThemeContext = createContext<{
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}>(null!);

function readTheme(value: string | null | undefined): Theme {
  return value === "light" || value === "auto" ? value : "dark";
}

export function useTheme() {
  return useContext(ThemeContext);
}

function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "auto"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
    : theme;
}

function applyTheme(choice: Theme) {
  const theme = resolveTheme(choice);
  document.documentElement.dataset.theme = theme;
  document.body.dataset.theme = theme;
  if (theme === "dark") document.body.setAttribute("arco-theme", "dark");
  else document.body.removeAttribute("arco-theme");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#000000" : "#f7faf6");
}

export function initializeTheme() {
  let theme: Theme = "dark";
  try {
    theme = readTheme(localStorage.getItem(storageKey));
  } catch {
    /* The default still works when storage is unavailable. */
  }
  applyTheme(theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return readTheme(localStorage.getItem(storageKey));
    } catch {
      return "dark";
    }
  });
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(theme));
  useLayoutEffect(() => {
    const update = () => {
      applyTheme(theme);
      setResolvedTheme(resolveTheme(theme));
    };
    update();
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    if (theme === "auto") media.addEventListener("change", update);
    try {
      localStorage.setItem(storageKey, theme);
    } catch {
      /* Keep the current choice in memory. */
    }
    return () => media.removeEventListener("change", update);
  }, [theme]);
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null)
        setTheme(readTheme(event.newValue));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme: () =>
          setTheme(resolvedTheme === "dark" ? "light" : "dark"),
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function ThemeToggle() {
  const { resolvedTheme: theme, toggleTheme } = useContext(ThemeContext);
  const label = theme === "dark" ? "切换到浅色模式" : "切换到深色模式";
  return (
    <button
      type="button"
      className="icon-button theme-toggle"
      aria-label={label}
      title={label}
      onClick={toggleTheme}
    >
      {theme === "dark" ? (
        <Sun size={21} strokeWidth={1.7} />
      ) : (
        <Moon size={21} strokeWidth={1.7} />
      )}
    </button>
  );
}
